import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { UserEntitlement } from '../types/user';
import { toCamelCase } from '../utils/caseConversion';

interface UseSubscriptionResult {
  entitlement: UserEntitlement | null;
  loading: boolean;
  error: string | null;
  hasPremiumAccess: boolean;
  isInTrial: boolean;
  trialDaysRemaining: number;
  refresh: () => Promise<void>;
}

export const useSubscription = (userId: string | undefined): UseSubscriptionResult => {
  const [entitlement, setEntitlement] = useState<UserEntitlement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEntitlement = useCallback(async () => {
    if (!userId) {
      setEntitlement(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use the view for computed fields
      const { data, error: fetchError } = await supabase
        .from('user_entitlements')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (fetchError) {
        // If view doesn't exist yet (migration not run), fall back to subscriptions table
        if (fetchError.code === '42P01') {
          console.warn('user_entitlements view not found, using subscriptions table');
          const { data: subData, error: subError } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', userId)
            .maybeSingle();

          if (subError && subError.code !== 'PGRST116') {
            throw subError;
          }

          if (subData) {
            const sub = toCamelCase(subData);
            // Compute premium access manually
            const now = new Date();
            const hasPremiumAccess =
              (sub.status === 'active' && (!sub.currentPeriodEnd || new Date(sub.currentPeriodEnd) > now)) ||
              (sub.status === 'trial' && sub.trialEndsAt && new Date(sub.trialEndsAt) > now) ||
              (sub.status === 'cancelled' && sub.currentPeriodEnd && new Date(sub.currentPeriodEnd) > now);

            const isInTrial = sub.status === 'trial' && sub.trialEndsAt && new Date(sub.trialEndsAt) > now;
            const trialDaysRemaining = isInTrial
              ? Math.ceil((new Date(sub.trialEndsAt).getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
              : 0;

            setEntitlement({
              ...sub,
              hasPremiumAccess: !!hasPremiumAccess,
              isInTrial: !!isInTrial,
              trialDaysRemaining,
            });
          } else {
            // No subscription record - user is on free tier
            setEntitlement({
              userId,
              status: 'none',
              tier: 'free',
              hasPremiumAccess: false,
              isInTrial: false,
              trialDaysRemaining: 0,
            });
          }
          setLoading(false);
          return;
        }
        throw fetchError;
      }

      if (data) {
        console.log('✅ Entitlement chargé depuis Supabase:', {
          status: data.status,
          tier: data.tier,
          hasPremiumAccess: data.has_premium_access
        });
        setEntitlement(toCamelCase(data));
      } else {
        // No subscription record - user is on free tier
        setEntitlement({
          userId,
          status: 'none',
          tier: 'free',
          hasPremiumAccess: false,
          isInTrial: false,
          trialDaysRemaining: 0,
        });
      }
    } catch (err: any) {
      console.error('❌ Error loading entitlement:', err);
      setError(err.message || 'Failed to load subscription');
      // Default to free tier on error
      setEntitlement({
        userId,
        status: 'none',
        tier: 'free',
        hasPremiumAccess: false,
        isInTrial: false,
        trialDaysRemaining: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadEntitlement();
  }, [loadEntitlement]);

  return {
    entitlement,
    loading,
    error,
    hasPremiumAccess: entitlement?.hasPremiumAccess ?? false,
    isInTrial: entitlement?.isInTrial ?? false,
    trialDaysRemaining: entitlement?.trialDaysRemaining ?? 0,
    refresh: loadEntitlement,
  };
};

export default useSubscription;
